import { Component, Inject } from '@angular/core';
import { FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ActivityBodyInterface,
  ActivityInterface,
} from '@sport-activities/sdk';

import { appConfig } from '~/app.config';
import { FormGroupValueInterface } from '~/component/form/interfaces/form-group-value.interface';
import { FormControlService } from '~/core/services/form/form-control.service';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { TitleService } from '~/core/services/title/title.service';
import { noHtmlTagValidator } from '~/core/validators/no-html-tag/no-html-tag.validator';
import { AbstractOrganizerActivityCreationFormPageDirective } from '~/organizer/activity-creation/components/shared/form-wrapper/classes/abstract-activity-creation-form-page';
import { NAVIGATION_ROUTING } from '~/organizer/shared/components/shared/navigation-buttons/navigation-buttons.component';
import { OrganizerSharedPageContainerTipConfigInterface } from '~/organizer/shared/components/shared/page-container/interfaces/tip.config.interface';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';

@Component({
  selector: 'app-organizer-activity-creation-title',
  templateUrl: './title.component.html',
  styleUrls: [
    '../../../../../organizer/shared/components/shared/form-wrapper/form-wrapper.component.scss',
  ],
})
export class OrganizerActivityCreationPageTitleComponent extends AbstractOrganizerActivityCreationFormPageDirective {
  protected readonly pageTitleKey =
    'seo.organizer.activity_creation.title.title';

  public readonly tipsConfig: OrganizerSharedPageContainerTipConfigInterface[] =
    [
      {
        descriptionKey: 'activity_creation.title.tips.description',
        headerKey: 'activity_creation.title.tips.header',
      },
    ];
  public readonly titleDefaultMaximumCharacters =
    appConfig.formValidationRules.activityTitleMaximumLength;

  constructor(
    private readonly locatableService: OrganizerSharedLocatableService,
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly activityService: OrganizerSharedActivityService,
    protected readonly formBuilder: FormBuilder,
    protected readonly navigationService: NavigationService,
    protected readonly organizationService: OrganizerSharedOrganizationService,
    @Inject(NAVIGATION_ROUTING)
    protected readonly navigationRouting: RoutingInterface[],
    protected readonly titleService: TitleService,
    private readonly formControlService: FormControlService,
    private readonly toggleService: ToggleService
  ) {
    super(
      activatedRoute,
      activityService,
      formBuilder,
      navigationService,
      organizationService,
      navigationRouting,
      titleService
    );
  }

  public get titleErrors(): ValidationErrors {
    const title = this.formGroup.get('title');

    return title?.touched && title?.errors;
  }

  public get titleMaximumCharacters(): number {
    return +this.toggleService.getVariant(
      ToggleNameEnum.ActivityTitleMaximumLength,
      `${this.titleDefaultMaximumCharacters}`
    );
  }

  protected buildActivityData(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityInterface> {
    return {
      name: formGroupValue.title,
    };
  }

  protected buildDataBeforeSubmit(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityBodyInterface> {
    return {
      name: [
        this.locatableService.buildCurrentLocatableBody(formGroupValue.title),
      ],
    };
  }

  protected buildValidationConfig(): void {
    const activity = this.activityService.activity;

    this.validationConfig = {
      title: [
        activity.name || '',
        [
          Validators.required,
          Validators.maxLength(this.titleMaximumCharacters),
          noHtmlTagValidator(),
        ],
      ],
    };
  }

  public submit(formGroupValue: FormGroupValueInterface): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    super.submit(formGroupValue);
  }
}
